import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountTreeOutlined as AccountTreeOutlinedIcon,
  ArrowOutwardRounded as ArrowOutwardRoundedIcon,
  BugReportOutlined as BugReportOutlinedIcon,
  ContentCopyOutlined as ContentCopyOutlinedIcon,
  FilterListRounded as FilterListRoundedIcon,
  KeyOutlined as KeyOutlinedIcon,
  NotesOutlined as NotesOutlinedIcon,
  RouteOutlined as RouteOutlinedIcon,
  SearchRounded as SearchRoundedIcon,
  TuneRounded as TuneRoundedIcon,
} from '@mui/icons-material';
import { alpha, lighten } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import Nav from './nav';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';

const METHOD_ORDER = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];
const VERIFY_THRESHOLD = 25;

const METHOD_COLORS = {
  GET: '#8ec5ff',
  POST: '#9dd7b0',
  PUT: '#ffd98a',
  DELETE: '#ffb59f',
  PATCH: '#d8b4f8',
};

const SEVERITY_META = {
  critical: { label: 'Critical', color: '#7b1fa2', order: 5 },
  high: { label: 'High', color: '#d32f2f', order: 4 },
  medium: { label: 'Medium', color: '#ef6c00', order: 3 },
  low: { label: 'Low', color: '#1976d2', order: 2 },
  info: { label: 'Info', color: '#2e7d32', order: 1 },
};

const surfaceCardSx = {
  borderRadius: 4,
  border: '1px solid',
  borderColor: alpha(PRIMARYCOLOR, 0.08),
  boxShadow: '0 18px 48px rgba(15, 23, 42, 0.06)',
  bgcolor: '#fff',
};

const toneMap = {
  blue: { color: PRIMARYCOLOR, bg: alpha(PRIMARYCOLOR, 0.08) },
  red: { color: '#d32f2f', bg: alpha('#d32f2f', 0.08) },
  amber: { color: '#ef6c00', bg: alpha('#ef6c00', 0.08) },
  green: { color: '#2e7d32', bg: alpha('#2e7d32', 0.08) },
  slate: { color: '#455a64', bg: alpha('#455a64', 0.08) },
  purple: { color: '#7b1fa2', bg: alpha('#7b1fa2', 0.08) },
};

const num = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeMethod = (value, fallback = '') => {
  const normalized = String(value || fallback || '').trim().toUpperCase();
  return normalized || fallback;
};

const normalizePath = (value, fallback = 'Unknown endpoint') => {
  const normalized = String(value || '').trim();
  return normalized || fallback;
};

const buildEndpointKey = (path, method = '') => {
  const normalizedPath = normalizePath(path);
  const normalizedMethod = normalizeMethod(method);
  return normalizedMethod ? `${normalizedMethod} ${normalizedPath}` : normalizedPath;
};

const severityKey = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized.includes('critical')) return 'critical';
  if (normalized.includes('high')) return 'high';
  if (normalized.includes('medium')) return 'medium';
  if (normalized.includes('low')) return 'low';
  return 'info';
};

const cleanParamToken = (value) => {
  const cleaned = String(value ?? '')
    .replace(/^['"`]+|['"`]+$/g, '')
    .trim();

  if (!cleaned) return '';
  if (cleaned === '[object Object]') return '';
  if (cleaned.toLowerCase() === 'null' || cleaned.toLowerCase() === 'undefined') return '';
  return cleaned;
};

const collectParamTokens = (value) => {
  if (value == null) return [];

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectParamTokens(item));
  }

  if (typeof value === 'object') {
    const preferredKeys = [
      'name',
      'key',
      'param',
      'parameter',
      'field',
      'variable',
      'query',
      'path',
      'body',
      'param_name',
      'query_param',
      'path_param',
      'body_param',
    ];
    const metadataKeys = new Set([
      'required',
      'optional',
      'description',
      'example',
      'examples',
      'type',
      'value',
      'default',
      'schema',
      'location',
      'in',
      'format',
      'nullable',
    ]);

    const entries = Object.entries(value);
    const tokens = [];
    let matchedPreferred = false;

    preferredKeys.forEach((key) => {
      if (key in value) {
        matchedPreferred = true;
        tokens.push(...collectParamTokens(value[key]));
      }
    });

    if (!matchedPreferred) {
      entries.forEach(([key, nestedValue]) => {
        const normalizedKey = cleanParamToken(key);
        if (!normalizedKey) return;
        if (!metadataKeys.has(normalizedKey.toLowerCase())) {
          tokens.push(normalizedKey);
        } else if (Array.isArray(nestedValue) || (nestedValue && typeof nestedValue === 'object')) {
          tokens.push(...collectParamTokens(nestedValue));
        }
      });
    }

    return tokens;
  }

  const raw = String(value).trim();
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (parsed !== value) {
      return collectParamTokens(parsed);
    }
  } catch {
    // fall through
  }

  if (raw.includes(',')) {
    return raw.split(',').flatMap((item) => collectParamTokens(item));
  }

  const token = cleanParamToken(raw);
  return token ? [token] : [];
};

const parseParamList = (value) => [...new Set(collectParamTokens(value))];

const stripQueryString = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  try {
    if (raw.includes('://')) {
      const parsed = new URL(raw);
      return `${parsed.pathname || '/'}${parsed.hash || ''}` || raw;
    }
  } catch {
    // ignore parse errors
  }

  return raw.split('?')[0];
};

const extractQueryKeysFromPath = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return [];

  try {
    if (raw.includes('://')) {
      const parsed = new URL(raw);
      return [...new Set(Array.from(parsed.searchParams.keys()).map((item) => item.trim()).filter(Boolean))];
    }
  } catch {
    // ignore parse errors
  }

  const queryIndex = raw.indexOf('?');
  if (queryIndex === -1) return [];

  return [
    ...new Set(
      Array.from(new URLSearchParams(raw.slice(queryIndex + 1)).keys())
        .map((item) => item.trim())
        .filter(Boolean)
    ),
  ];
};

const extractRouteParamKeys = (value) => {
  const raw = stripQueryString(value);
  if (!raw) return [];

  const matches = [
    ...Array.from(raw.matchAll(/:([A-Za-z0-9_-]+)/g)).map((match) => match[1]),
    ...Array.from(raw.matchAll(/\{([A-Za-z0-9_-]+)\}/g)).map((match) => match[1]),
    ...Array.from(raw.matchAll(/<([A-Za-z0-9_-]+)>/g)).map((match) => match[1]),
  ];

  return [...new Set(matches.map((item) => item.trim()).filter(Boolean))];
};

const parseLocation = (value) => {
  if (!value) return { path: '', method: '' };

  if (Array.isArray(value)) {
    return parseLocation(value.find(Boolean) || '');
  }

  const raw = String(value).trim();
  if (!raw) return { path: '', method: '' };

  if (raw.startsWith('[') && raw.endsWith(']')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parseLocation(parsed.find(Boolean) || '');
    } catch {
      // ignore parse errors
    }
  }

  const parts = raw.split(' - ');
  if (parts.length >= 2) {
    const method = normalizeMethod(parts[parts.length - 1]);
    if (METHOD_ORDER.includes(method)) {
      return {
        path: normalizePath(parts.slice(0, -1).join(' - ')),
        method,
      };
    }
  }

  const matched = raw.match(/^(.*)\s+-\s+([A-Z]+)$/i);
  if (matched) {
    const method = normalizeMethod(matched[2]);
    if (METHOD_ORDER.includes(method)) {
      return {
        path: normalizePath(matched[1]),
        method,
      };
    }
  }

  return { path: normalizePath(raw), method: '' };
};

const copyText = async (value = '') => {
  try {
    await navigator.clipboard.writeText(String(value));
  } catch {
    // ignore copy errors
  }
};

const CodeBlock = ({ language, value }) => (
  <Box sx={{ position: 'relative' }}>
    <IconButton
      size="small"
      onClick={() => copyText(value)}
      sx={{
        position: 'absolute',
        right: 8,
        top: 8,
        zIndex: 1,
        bgcolor: 'rgba(255,255,255,0.08)',
        color: '#fff',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
      }}
      title="Copy"
    >
      <ContentCopyOutlinedIcon fontSize="inherit" />
    </IconButton>
    <SyntaxHighlighter
      language={language || 'bash'}
      style={prism}
      wrapLines
      customStyle={{
        borderRadius: 12,
        margin: 0,
        fontSize: '0.9rem',
        background: '#0f172a',
        color: '#e2e8f0',
        paddingTop: 24,
      }}
      codeTagProps={{ style: { fontFamily: 'Consolas, Monaco, monospace' } }}
    >
      {value}
    </SyntaxHighlighter>
  </Box>
);

const mdComponents = {
  h1: (props) => <Typography variant="h6" sx={{ mt: 1.5, mb: 1, fontWeight: 900, color: '#10243E' }} {...props} />,
  h2: (props) => <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.75, fontWeight: 900, color: '#10243E' }} {...props} />,
  p: (props) => <Typography variant="body2" sx={{ mb: 1.1, lineHeight: 1.7, color: '#31445F' }} {...props} />,
  li: (props) => (
    <li style={{ marginLeft: 18, color: '#31445F' }}>
      <Typography component="span" variant="body2" {...props} />
    </li>
  ),
  code: ({ inline, className, children, ...props }) => {
    if (inline) {
      return (
        <code
          style={{
            background: '#f5f7fb',
            border: '1px solid #e3e8f2',
            borderRadius: 4,
            padding: '1px 4px',
            fontFamily: 'monospace',
            fontSize: '0.85em',
          }}
          {...props}
        >
          {children}
        </code>
      );
    }

    const language = (className || '').replace('language-', '') || 'bash';
    return <CodeBlock language={language} value={String(children).replace(/\n$/, '')} />;
  },
};

const MethodChip = ({ method }) => (
  <Chip
    size="small"
    label={method}
    sx={{
      bgcolor: METHOD_COLORS[method] || alpha(PRIMARYCOLOR, 0.12),
      color: '#10243E',
      fontWeight: 900,
      minWidth: 64,
      justifyContent: 'center',
    }}
  />
);

const SeverityChipGroup = ({ counts }) => {
  const active = ['critical', 'high', 'medium', 'low', 'info'].filter((key) => num(counts?.[key], 0) > 0);

  if (!active.length) {
    return <Typography variant="body2" color="text.secondary">No findings yet</Typography>;
  }

  return (
    <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
      {active.map((key) => (
        <Chip
          key={key}
          size="small"
          label={`${SEVERITY_META[key].label} ${num(counts[key], 0)}`}
          sx={{
            fontWeight: 800,
            bgcolor: alpha(SEVERITY_META[key].color, 0.1),
            color: SEVERITY_META[key].color,
          }}
        />
      ))}
    </Stack>
  );
};

const SectionCard = ({ title, subtitle, action, children, sx }) => (
  <Card sx={{ ...surfaceCardSx, p: 3, ...sx }}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      sx={{ mb: 2 }}
    >
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 900, color: '#10243E' }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Stack>
    {children}
  </Card>
);

const MetricCard = ({ label, value, caption, icon: Icon, tone = 'blue' }) => {
  const toneStyle = toneMap[tone] || toneMap.blue;

  return (
    <Card sx={{ ...surfaceCardSx, p: 2.1, height: '100%' }}>
      <Stack direction="row" spacing={1.35} alignItems="flex-start">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            bgcolor: toneStyle.bg,
            color: toneStyle.color,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', letterSpacing: 0.3 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, color: '#10243E', lineHeight: 1.1, mt: 0.35 }}>
            {value}
          </Typography>
          {caption ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.65, lineHeight: 1.45 }}>
              {caption}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Card>
  );
};

const InsightBar = ({ label, value, max = 1, color = PRIMARYCOLOR, secondary }) => {
  const percent = Math.min(100, Math.round((value / Math.max(1, max)) * 100));

  return (
    <Stack spacing={0.55}>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#10243E', minWidth: 0 }} noWrap title={label}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {secondary || value}
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percent}
        sx={{
          height: 8,
          borderRadius: 999,
          bgcolor: alpha(color, 0.12),
          '& .MuiLinearProgress-bar': { borderRadius: 999, bgcolor: color },
        }}
      />
    </Stack>
  );
};

const ParamBlock = ({ title, items, color }) => (
  <Box
    sx={{
      p: 1.6,
      borderRadius: 3,
      border: '1px solid',
      borderColor: alpha(color, 0.12),
      bgcolor: alpha(color, 0.035),
      height: '100%',
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {title}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: 900, color: '#10243E', mt: 0.35 }}>
      {items.length}
    </Typography>
    <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75, mt: 1 }}>
      {items.length ? (
        items.map((item) => <Chip key={`${title}-${item}`} size="small" label={item} variant="outlined" />)
      ) : (
        <Typography variant="body2" color="text.secondary">No parameters</Typography>
      )}
    </Stack>
  </Box>
);

const InputPreviewGroup = ({ label, items, color }) => (
  <Box
    sx={{
      p: 1.15,
      borderRadius: 2.5,
      border: '1px solid',
      borderColor: alpha(color, 0.12),
      bgcolor: alpha(color, 0.04),
      height: '100%',
    }}
  >
    <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.85 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Chip size="small" label={items.length} sx={{ height: 22, fontWeight: 700 }} />
    </Stack>

    {items.length ? (
      <Stack direction="row" spacing={0.6} sx={{ flexWrap: 'wrap', rowGap: 0.6 }}>
        {items.slice(0, 6).map((item) => (
          <Chip
            key={`${label}-${item}`}
            size="small"
            label={item}
            variant="outlined"
            sx={{
              maxWidth: '100%',
              '& .MuiChip-label': {
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              },
            }}
          />
        ))}
        {items.length > 6 ? <Chip size="small" label={`+${items.length - 6}`} variant="outlined" /> : null}
      </Stack>
    ) : (
      <Typography variant="body2" color="text.secondary">
        None
      </Typography>
    )}
  </Box>
);

const WebPentestAssets = () => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();

  const [endpoints, setEndpoints] = useState([]);
  const [exploits, setExploits] = useState([]);
  const [threatEndpoints, setThreatEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState('ALL');
  const [quickFilter, setQuickFilter] = useState('ALL');
  const [selectedKey, setSelectedKey] = useState('');

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return undefined;
    }

    let cancelled = false;
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    const fetchJson = (url) => fetch(url, { headers }).then((res) => res.json());

    (async () => {
      try {
        setLoading(true);
        setError('');

        const [endpointsData, exploitsData, threatData] = await Promise.all([
          fetchJson(`${BASEURL}/pentests/web/endpoints?pentest_id=${pentest_id}`),
          fetchJson(`${BASEURL}/pentests/web/exploits?pentest_id=${pentest_id}`),
          fetchJson(`${BASEURL}/pentests/web/threat_model?pentest_id=${pentest_id}`),
        ]);

        if (cancelled) return;

        const invalidToken = [endpointsData, exploitsData, threatData].some(
          (data) => data?.message === 'Invalid token'
        );
        if (invalidToken) {
          navigate('/');
          return;
        }

        setEndpoints(Array.isArray(endpointsData?.endpoints) ? endpointsData.endpoints : []);
        setExploits(Array.isArray(exploitsData?.exploits) ? exploitsData.exploits : []);
        setThreatEndpoints(
          Array.isArray(threatData?.threat_model?.endpoints) ? threatData.threat_model.endpoints : []
        );
      } catch (fetchError) {
        console.error(fetchError);
        if (!cancelled) setError('Failed to load the web endpoint inventory right now.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, pentest_id]);

  const endpointRows = useMemo(() => {
    const map = new Map();

    const ensureEntry = (path, method, seed = {}) => {
      const resolvedPath = normalizePath(path, `Endpoint ${map.size + 1}`);
      const resolvedMethod = normalizeMethod(method, 'GET');
      const key = buildEndpointKey(resolvedPath, resolvedMethod);

      if (!map.has(key)) {
        map.set(key, {
          key,
          path: resolvedPath,
          method: resolvedMethod,
          domain: '',
          summary: '',
          bodyParams: [],
          queryParams: [],
          pathParams: [],
          inventoryCounts: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
          inventoryTotalFindings: 0,
          matchedFindings: [],
          matchedAttacks: [],
          threatNarrative: '',
          sourceIds: [],
          ...seed,
        });
      }

      return map.get(key);
    };

    endpoints.forEach((endpoint, index) => {
      const method = normalizeMethod(endpoint?.request_type, 'GET');
      const path = normalizePath(endpoint?.path || endpoint?.endpoint || endpoint?.url, `Endpoint ${index + 1}`);
      const entry = ensureEntry(path, method);

      entry.domain = endpoint?.domain || entry.domain;
      entry.summary = endpoint?.summary || entry.summary;
      entry.bodyParams = parseParamList(endpoint?.body_param).length
        ? parseParamList(endpoint?.body_param)
        : entry.bodyParams;
      entry.queryParams = parseParamList(endpoint?.query_param).length
        ? parseParamList(endpoint?.query_param)
        : entry.queryParams;
      entry.pathParams = parseParamList(endpoint?.path_param).length
        ? parseParamList(endpoint?.path_param)
        : entry.pathParams;
      entry.inventoryTotalFindings = Math.max(entry.inventoryTotalFindings, num(endpoint?.total_exploits_found, 0));
      entry.inventoryCounts = {
        critical: Math.max(entry.inventoryCounts.critical, num(endpoint?.critical_exploits_found, 0)),
        high: Math.max(entry.inventoryCounts.high, num(endpoint?.high_exploits_found, 0)),
        medium: Math.max(entry.inventoryCounts.medium, num(endpoint?.medium_exploits_found, 0)),
        low: Math.max(entry.inventoryCounts.low, num(endpoint?.low_exploits_found, 0)),
        info: Math.max(entry.inventoryCounts.info, num(endpoint?.info_exploits_found, 0)),
      };
      if (endpoint?.id != null) entry.sourceIds.push(String(endpoint.id));
    });

    threatEndpoints.forEach((endpoint) => {
      const method = normalizeMethod(endpoint?.http_method, 'GET');
      const path = normalizePath(endpoint?.path);
      const entry = ensureEntry(path, method);

      const attacks = Array.isArray(endpoint?.threat_model?.attacks) ? endpoint.threat_model.attacks : [];
      entry.matchedAttacks = [...attacks].sort((left, right) => num(right?.score, 0) - num(left?.score, 0));
      entry.threatNarrative = endpoint?.threat_model?.threat_model || entry.threatNarrative;
    });

    exploits.forEach((finding, index) => {
      const location = parseLocation(
        finding?.impacted_assets || finding?.endpoint || finding?.asset || finding?.location || ''
      );

      let entry;
      if (location.path && location.method) {
        entry = ensureEntry(location.path, location.method);
      } else if (location.path) {
        entry = Array.from(map.values()).find((row) => row.path === location.path) || ensureEntry(location.path, 'GET');
      } else {
        entry = ensureEntry(`Endpoint Finding ${index + 1}`, 'GET');
      }

      entry.matchedFindings.push(finding);
    });

    return Array.from(map.values())
      .map((row) => {
        const matchedSeverityCounts = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
        row.matchedFindings.forEach((finding) => {
          matchedSeverityCounts[severityKey(finding?.severity)] += 1;
        });

        const severityCounts = {
          critical: Math.max(row.inventoryCounts.critical, matchedSeverityCounts.critical),
          high: Math.max(row.inventoryCounts.high, matchedSeverityCounts.high),
          medium: Math.max(row.inventoryCounts.medium, matchedSeverityCounts.medium),
          low: Math.max(row.inventoryCounts.low, matchedSeverityCounts.low),
          info: Math.max(row.inventoryCounts.info, matchedSeverityCounts.info),
        };

        const severityTotal = Object.values(severityCounts).reduce((sum, value) => sum + value, 0);
        const totalFindings = Math.max(row.inventoryTotalFindings, row.matchedFindings.length, severityTotal);
        const inlineQueryParams = extractQueryKeysFromPath(row.path);
        const inlinePathParams = extractRouteParamKeys(row.path);
        const bodyParams = [...new Set(row.bodyParams)];
        const queryParams = [...new Set([...row.queryParams, ...inlineQueryParams])];
        const pathParams = [...new Set([...row.pathParams, ...inlinePathParams])];
        const displayPath = stripQueryString(row.path) || row.path;
        const paramPreview = [...pathParams, ...queryParams, ...bodyParams];
        const paramTotal = bodyParams.length + queryParams.length + pathParams.length;
        const likelyAttacks = row.matchedAttacks.filter((attack) => num(attack?.score, 0) >= VERIFY_THRESHOLD);
        const topAttackScore = row.matchedAttacks.reduce((max, attack) => Math.max(max, num(attack?.score, 0)), 0);
        const highestSeverity =
          ['critical', 'high', 'medium', 'low', 'info'].find((key) => severityCounts[key] > 0) || 'info';
        const summary = String(row.summary || '').trim();
        const hasSummary =
          Boolean(summary) &&
          summary !== '_No summary available for this endpoint._' &&
          summary.toLowerCase() !== 'no summary available for this endpoint.';
        const stateChanging = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(row.method);
        const summaryPreview = hasSummary
          ? summary
              .replace(/[#>*_`-]/g, ' ')
              .replace(/\s+/g, ' ')
              .trim()
              .slice(0, 150)
          : '';
        const riskIndex =
          totalFindings * 14 +
          likelyAttacks.length * 5 +
          topAttackScore * 0.25 +
          paramTotal * 0.8 +
          (stateChanging ? 4 : 0) +
          (hasSummary ? 1 : 0);

        return {
          ...row,
          displayPath,
          bodyParams,
          queryParams,
          pathParams,
          paramPreview,
          paramTotal,
          likelyAttacks,
          likelyAttackCount: likelyAttacks.length,
          topAttackScore,
          severityCounts,
          totalFindings,
          highestSeverity,
          hasSummary,
          summaryPreview,
          stateChanging,
          riskIndex,
        };
      })
      .sort((left, right) => {
        if (right.riskIndex !== left.riskIndex) return right.riskIndex - left.riskIndex;
        if (SEVERITY_META[right.highestSeverity].order !== SEVERITY_META[left.highestSeverity].order) {
          return SEVERITY_META[right.highestSeverity].order - SEVERITY_META[left.highestSeverity].order;
        }
        return left.path.localeCompare(right.path);
      });
  }, [endpoints, exploits, threatEndpoints]);

  const selectedEndpoint = useMemo(
    () => endpointRows.find((row) => row.key === selectedKey) || null,
    [endpointRows, selectedKey]
  );

  const methodCounts = useMemo(() => {
    const counts = { GET: 0, POST: 0, PUT: 0, DELETE: 0, PATCH: 0 };
    endpointRows.forEach((row) => {
      counts[row.method] = (counts[row.method] || 0) + 1;
    });
    return counts;
  }, [endpointRows]);

  const totalEndpoints = endpointRows.length;
  const endpointsWithFindings = endpointRows.filter((row) => row.totalFindings > 0).length;
  const endpointsWithParams = endpointRows.filter((row) => row.paramTotal > 0).length;
  const stateChangingRoutes = endpointRows.filter((row) => row.stateChanging).length;
  const endpointsWithLikelyPaths = endpointRows.filter((row) => row.likelyAttackCount > 0).length;
  const endpointsWithSummaries = endpointRows.filter((row) => row.hasSummary).length;
  const activeMethodCount = METHOD_ORDER.filter((method) => num(methodCounts[method], 0) > 0).length;
  const summaryCoverage = totalEndpoints ? Math.round((endpointsWithSummaries / totalEndpoints) * 100) : 0;
  const likelyPathTotal = endpointRows.reduce((sum, row) => sum + row.likelyAttackCount, 0);

  const topRiskRows = useMemo(() => {
    const list = endpointRows.filter((row) => row.riskIndex > 0).slice(0, 6);
    const max = Math.max(1, ...list.map((row) => row.riskIndex));
    return {
      max,
      rows: list.map((row) => ({
        key: row.key,
        label: `${row.method} ${row.displayPath || row.path}`,
        value: row.riskIndex,
        secondary: `${row.totalFindings} finding${row.totalFindings === 1 ? '' : 's'} • ${row.likelyAttackCount} likely path${row.likelyAttackCount === 1 ? '' : 's'}`,
      })),
    };
  }, [endpointRows]);

  const parameterRows = useMemo(() => {
    const list = [...endpointRows]
      .sort((left, right) => right.paramTotal - left.paramTotal)
      .filter((row) => row.paramTotal > 0)
      .slice(0, 6);
    const max = Math.max(1, ...list.map((row) => row.paramTotal));
    return {
      max,
      rows: list.map((row) => ({
        key: row.key,
        label: `${row.method} ${row.displayPath || row.path}`,
        value: row.paramTotal,
        secondary: row.paramPreview.slice(0, 3).join(', ') || `${row.paramTotal} inputs`,
      })),
    };
  }, [endpointRows]);

  const filteredRows = useMemo(() => {
    const query = String(searchTerm || '').trim().toLowerCase();

    return endpointRows.filter((row) => {
      const matchesMethod = methodFilter === 'ALL' || row.method === methodFilter;
      const matchesQuick =
        quickFilter === 'ALL' ||
        (quickFilter === 'HAS_FINDINGS' && row.totalFindings > 0) ||
        (quickFilter === 'LIKELY_PATHS' && row.likelyAttackCount > 0) ||
        (quickFilter === 'HAS_PARAMS' && row.paramTotal > 0) ||
        (quickFilter === 'STATE_CHANGING' && row.stateChanging) ||
        (quickFilter === 'HAS_SUMMARY' && row.hasSummary);

      const searchText = [
        row.method,
        row.path,
        row.domain,
        row.summary,
        row.bodyParams.join(' '),
        row.queryParams.join(' '),
        row.pathParams.join(' '),
        row.matchedFindings.map((finding) => finding?.name).join(' '),
        row.likelyAttacks.map((attack) => attack?.name).join(' '),
      ]
        .join(' ')
        .toLowerCase();

      const matchesSearch = !query || searchText.includes(query);

      return matchesMethod && matchesQuick && matchesSearch;
    });
  }, [endpointRows, methodFilter, quickFilter, searchTerm]);

  const drawerFindingRows = useMemo(
    () =>
      (selectedEndpoint?.matchedFindings || [])
        .map((finding, index) => ({
          id: `${finding?.name || 'finding'}-${index}`,
          name: finding?.name || `Finding ${index + 1}`,
          severity: severityKey(finding?.severity),
        }))
        .sort((left, right) => SEVERITY_META[right.severity].order - SEVERITY_META[left.severity].order),
    [selectedEndpoint]
  );

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: lighten(PRIMARYCOLOR, 0.985) }}>
      <Nav />

      <Box sx={{ flexGrow: 1, px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 }, overflowY: 'auto' }}>
        <Stack spacing={2.25}>
          <SectionCard
            title="Web Endpoint Inventory"
            subtitle="Review discovered routes, parameter exposure, likely attack paths, and validated findings across the application surface."
            action={(
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Button
                  variant="outlined"
                  endIcon={<ArrowOutwardRoundedIcon />}
                  onClick={() => navigate(`/pentest/web/threat_model/${pentest_id}`)}
                  sx={{ borderRadius: 999 }}
                >
                  Threat Model
                </Button>
                <Button
                  variant="contained"
                  endIcon={<ArrowOutwardRoundedIcon />}
                  onClick={() => navigate(`/pentest/web/exploits/${pentest_id}`)}
                  sx={{ borderRadius: 999, boxShadow: 'none' }}
                >
                  Findings
                </Button>
              </Stack>
            )}
          >
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
              <Chip label={`${totalEndpoints} inventoried endpoints`} variant="outlined" />
              <Chip label={`${activeMethodCount} active HTTP methods`} variant="outlined" />
              <Chip label={`${endpointsWithLikelyPaths} endpoints with likely paths`} variant="outlined" />
              <Chip label={`${endpointsWithFindings} endpoints with findings`} variant="outlined" />
              <Chip label={`${summaryCoverage}% summary coverage`} variant="outlined" />
            </Stack>
          </SectionCard>

          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6} lg={2}>
              <MetricCard
                label="Total Endpoints"
                value={totalEndpoints}
                caption="Routes discovered during enumeration and testing."
                icon={RouteOutlinedIcon}
                tone="blue"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <MetricCard
                label="Findings Present"
                value={endpointsWithFindings}
                caption="Endpoints already tied to validated issues."
                icon={BugReportOutlinedIcon}
                tone="red"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <MetricCard
                label="State-Changing"
                value={stateChangingRoutes}
                caption="POST, PUT, PATCH, and DELETE routes."
                icon={TuneRoundedIcon}
                tone="amber"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <MetricCard
                label="Parameterized"
                value={endpointsWithParams}
                caption="Endpoints exposing path, query, or body input."
                icon={KeyOutlinedIcon}
                tone="purple"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <MetricCard
                label="Likely Paths"
                value={endpointsWithLikelyPaths}
                caption={`${likelyPathTotal} prioritized attack path${likelyPathTotal === 1 ? '' : 's'} across the model.`}
                icon={AccountTreeOutlinedIcon}
                tone="slate"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <MetricCard
                label="Summaries Ready"
                value={`${summaryCoverage}%`}
                caption="Narrative coverage available for triage and review."
                icon={NotesOutlinedIcon}
                tone="green"
              />
            </Grid>
          </Grid>

          <Box sx={{ pt: 3.5 }}>
            <SectionCard
              title="Inventory Signals"
              subtitle="Compact route signals so you can get to the main inventory faster without losing the important context."
              sx={{ py: 2.2, mt: 0 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} xl={4}>
                  <Stack spacing={0.9}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E' }}>
                      Route Risk
                    </Typography>
                    {topRiskRows.rows.length ? (
                      topRiskRows.rows.slice(0, 4).map((row) => (
                        <Box key={row.key} onClick={() => setSelectedKey(row.key)} sx={{ cursor: 'pointer' }}>
                          <InsightBar
                            label={row.label}
                            value={row.value}
                            max={topRiskRows.max}
                            color="#d32f2f"
                            secondary={row.secondary}
                          />
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Risk-ranked routes will appear here as findings and threat-model coverage expand.
                      </Typography>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} xl={4}>
                  <Stack spacing={0.9}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E' }}>
                      Method Coverage
                    </Typography>
                    {METHOD_ORDER.map((method) => (
                      <InsightBar
                        key={method}
                        label={method}
                        value={num(methodCounts[method], 0)}
                        max={Math.max(1, ...METHOD_ORDER.map((item) => num(methodCounts[item], 0)))}
                        color={METHOD_COLORS[method] || PRIMARYCOLOR}
                        secondary={`${num(methodCounts[method], 0)} route${num(methodCounts[method], 0) === 1 ? '' : 's'}`}
                      />
                    ))}
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, pt: 0.5 }}>
                      <Chip size="small" label={`${activeMethodCount} active methods`} variant="outlined" />
                      <Chip
                        size="small"
                        label={`${totalEndpoints ? Math.round((stateChangingRoutes / totalEndpoints) * 100) : 0}% state-changing`}
                        variant="outlined"
                      />
                      <Chip size="small" label={`${summaryCoverage}% summaries ready`} variant="outlined" />
                    </Stack>
                  </Stack>
                </Grid>

                <Grid item xs={12} xl={4}>
                  <Stack spacing={0.9}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E' }}>
                      Parameter Exposure
                    </Typography>
                    {parameterRows.rows.length ? (
                      parameterRows.rows.slice(0, 4).map((row) => (
                        <Box key={row.key} onClick={() => setSelectedKey(row.key)} sx={{ cursor: 'pointer' }}>
                          <InsightBar
                            label={row.label}
                            value={row.value}
                            max={parameterRows.max}
                            color="#7b1fa2"
                            secondary={row.secondary}
                          />
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Parameterized routes will populate here once endpoint metadata is available.
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ pt: 0.6 }}>
                      {totalEndpoints - endpointsWithSummaries > 0
                        ? `${totalEndpoints - endpointsWithSummaries} route${totalEndpoints - endpointsWithSummaries === 1 ? '' : 's'} still need summaries.`
                        : 'Summary coverage is in good shape for triage.'}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </SectionCard>
          </Box>

          <SectionCard
            title="Endpoint Inventory"
            subtitle="Filter by method or route characteristics, then open a route to review its summary, parameters, likely attacks, and findings."
            action={(
              <Chip
                size="small"
                label={`${filteredRows.length}/${totalEndpoints} shown`}
                variant="outlined"
                sx={{ fontWeight: 800 }}
              />
            )}
          >
            <Stack spacing={1.25}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.25} justifyContent="space-between">
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  {[
                    { key: 'ALL', label: 'All' },
                    { key: 'HAS_FINDINGS', label: 'Has Findings' },
                    { key: 'LIKELY_PATHS', label: 'Likely Paths' },
                    { key: 'HAS_PARAMS', label: 'Has Params' },
                    { key: 'STATE_CHANGING', label: 'State Changing' },
                    { key: 'HAS_SUMMARY', label: 'Has Summary' },
                  ].map((filter) => (
                    <Chip
                      key={filter.key}
                      label={filter.label}
                      onClick={() => setQuickFilter(filter.key)}
                      color={quickFilter === filter.key ? 'primary' : 'default'}
                      variant={quickFilter === filter.key ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 700 }}
                    />
                  ))}
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Method</InputLabel>
                    <Select value={methodFilter} label="Method" onChange={(event) => setMethodFilter(event.target.value)}>
                      <MenuItem value="ALL">All Methods</MenuItem>
                      {METHOD_ORDER.filter((method) => num(methodCounts[method], 0) > 0).map((method) => (
                        <MenuItem key={method} value={method}>{method}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    size="small"
                    placeholder="Search method, path, summary, params, or findings..."
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    sx={{ minWidth: { xs: '100%', md: 320 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Tooltip title="Reset filters">
                    <IconButton
                      onClick={() => {
                        setSearchTerm('');
                        setMethodFilter('ALL');
                        setQuickFilter('ALL');
                      }}
                    >
                      <FilterListRoundedIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>

              {loading ? (
                <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>
              ) : filteredRows.length ? (
                <Stack spacing={1.3}>
                  {filteredRows.map((row) => (
                    <Card
                      key={row.key}
                      sx={{
                        ...surfaceCardSx,
                        p: 2.1,
                        borderColor: selectedKey === row.key ? alpha(PRIMARYCOLOR, 0.24) : alpha(PRIMARYCOLOR, 0.08),
                        boxShadow: selectedKey === row.key
                          ? '0 20px 60px rgba(10, 92, 197, 0.12)'
                          : '0 14px 40px rgba(15, 23, 42, 0.05)',
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', lg: 'row' }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', lg: 'center' }}
                      >
                        <Stack direction="row" spacing={1.2} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              bgcolor: alpha(PRIMARYCOLOR, 0.1),
                              color: PRIMARYCOLOR,
                              fontWeight: 900,
                              flexShrink: 0,
                            }}
                          >
                            {(row.method || 'W').charAt(0)}
                          </Avatar>

                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.8 }}>
                              <MethodChip method={row.method} />
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 900,
                                  color: '#10243E',
                                  overflowWrap: 'anywhere',
                                  wordBreak: 'break-word',
                                  lineHeight: 1.45,
                                }}
                              >
                                {row.displayPath || row.path}
                              </Typography>
                            </Stack>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.45, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                            >
                              {row.domain || 'Route discovered during web enumeration'}
                            </Typography>

                            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75, mt: 1 }}>
                              {row.stateChanging ? <Chip size="small" label="State changing" variant="outlined" /> : null}
                              <Chip
                                size="small"
                                label={row.hasSummary ? 'Summary ready' : 'Summary missing'}
                                sx={{
                                  bgcolor: row.hasSummary ? alpha('#2e7d32', 0.08) : alpha('#455a64', 0.08),
                                  color: row.hasSummary ? '#2e7d32' : '#455a64',
                                  fontWeight: 700,
                                }}
                              />
                              <Chip size="small" label={`${row.paramTotal} input${row.paramTotal === 1 ? '' : 's'}`} variant="outlined" />
                              <Chip size="small" label={`${row.totalFindings} finding${row.totalFindings === 1 ? '' : 's'}`} variant="outlined" />
                            </Stack>
                          </Box>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', lg: 'auto' } }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setSelectedKey(row.key)}
                            sx={{ borderRadius: 999 }}
                          >
                            Review
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate(`/pentest/web/threat_model/${pentest_id}`)}
                            sx={{ borderRadius: 999, boxShadow: 'none' }}
                          >
                            Threat Model
                          </Button>
                        </Stack>
                      </Stack>

                      <Divider sx={{ my: 1.6 }} />

                      <Grid container spacing={1.5}>
                        <Grid item xs={12} xl={4}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 0.8 }}>
                            Summary
                          </Typography>
                          <Box
                            sx={{
                              p: 1.45,
                              borderRadius: 3,
                              bgcolor: alpha(PRIMARYCOLOR, 0.03),
                              border: '1px solid',
                              borderColor: alpha(PRIMARYCOLOR, 0.08),
                              minHeight: 116,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                              {row.summaryPreview
                                ? `${row.summaryPreview}${row.summaryPreview.length >= 150 ? '...' : ''}`
                                : 'No summary has been generated for this route yet. Open the review drawer for the full route context when available.'}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={12} xl={4}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 0.8 }}>
                            Input Surface
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                              <InputPreviewGroup label="Path" items={row.pathParams} color="#ef6c00" />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <InputPreviewGroup label="Query" items={row.queryParams} color={PRIMARYCOLOR} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <InputPreviewGroup label="Body" items={row.bodyParams} color="#7b1fa2" />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item xs={12} xl={4}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 0.8 }}>
                            Assessment Signals
                          </Typography>
                          <Box
                            sx={{
                              p: 1.45,
                              borderRadius: 3,
                              bgcolor: alpha('#455a64', 0.035),
                              border: '1px solid',
                              borderColor: alpha('#455a64', 0.1),
                              minHeight: 116,
                            }}
                          >
                            <Stack spacing={0.9}>
                              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
                                <Chip
                                  size="small"
                                  label={
                                    row.likelyAttackCount
                                      ? `${row.likelyAttackCount} likely path${row.likelyAttackCount === 1 ? '' : 's'}`
                                      : 'No prioritized paths yet'
                                  }
                                  sx={{
                                    bgcolor: row.likelyAttackCount ? alpha('#455a64', 0.08) : alpha('#90a4ae', 0.08),
                                    color: row.likelyAttackCount ? '#455a64' : '#607d8b',
                                    fontWeight: 700,
                                  }}
                                />
                                <Chip
                                  size="small"
                                  label={`Top score ${row.topAttackScore || 0}`}
                                  variant="outlined"
                                />
                              </Stack>

                              <SeverityChipGroup counts={row.severityCounts} />

                              {row.likelyAttacks.length ? (
                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                  Modeled paths: {row.likelyAttacks.slice(0, 2).map((attack) => attack?.name).filter(Boolean).join(', ')}
                                  {row.likelyAttacks.length > 2 ? '...' : ''}
                                </Typography>
                              ) : null}
                            </Stack>
                          </Box>
                        </Grid>
                      </Grid>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Box
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    border: '1px dashed',
                    borderColor: alpha(PRIMARYCOLOR, 0.16),
                    textAlign: 'center',
                    bgcolor: alpha(PRIMARYCOLOR, 0.02),
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#10243E' }}>
                    No endpoints match the current filters.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Try clearing the filters or using a broader search term.
                  </Typography>
                </Box>
              )}
            </Stack>
          </SectionCard>
        </Stack>
      </Box>

      <Drawer
        anchor="right"
        open={Boolean(selectedEndpoint)}
        onClose={() => setSelectedKey('')}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 560 },
            bgcolor: lighten(PRIMARYCOLOR, 0.988),
            p: 2.2,
          },
        }}
      >
        {selectedEndpoint ? (
          <Stack spacing={2}>
            <Box
              sx={{
                ...surfaceCardSx,
                p: 2.4,
                bgcolor: '#fff',
              }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <MethodChip method={selectedEndpoint.method} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 900,
                        color: '#10243E',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                      }}
                    >
                      {selectedEndpoint.displayPath || selectedEndpoint.path}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.75, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                  >
                    {selectedEndpoint.domain || 'Route discovered during application enumeration'}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1}>
                  <Tooltip title="Copy route">
                    <IconButton
                      size="small"
                      onClick={() => copyText(`${selectedEndpoint.method} ${selectedEndpoint.displayPath || selectedEndpoint.path}`)}
                    >
                      <ContentCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<ArrowOutwardRoundedIcon />}
                    onClick={() => navigate(`/pentest/web/threat_model/${pentest_id}`)}
                    sx={{ borderRadius: 999 }}
                  >
                    Threat Model
                  </Button>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mt: 1.5 }}>
                <Chip label={`${selectedEndpoint.totalFindings} finding${selectedEndpoint.totalFindings === 1 ? '' : 's'}`} />
                <Chip label={`${selectedEndpoint.likelyAttackCount} likely path${selectedEndpoint.likelyAttackCount === 1 ? '' : 's'}`} />
                <Chip label={`${selectedEndpoint.paramTotal} input${selectedEndpoint.paramTotal === 1 ? '' : 's'}`} />
                <Chip label={selectedEndpoint.hasSummary ? 'Summary ready' : 'Summary missing'} />
              </Stack>
            </Box>

            <Grid container spacing={1.2}>
              <Grid item xs={6}>
                <MetricCard
                  label="Inputs"
                  value={selectedEndpoint.paramTotal}
                  caption="Observed path, query, and body parameters."
                  icon={KeyOutlinedIcon}
                  tone="purple"
                />
              </Grid>
              <Grid item xs={6}>
                <MetricCard
                  label="Top Attack Score"
                  value={selectedEndpoint.topAttackScore || 0}
                  caption="Highest modeled likelihood score for this route."
                  icon={AccountTreeOutlinedIcon}
                  tone="slate"
                />
              </Grid>
            </Grid>

            <SectionCard
              title="Endpoint Summary"
              subtitle="Narrative and example context captured for this route."
              action={selectedEndpoint.summary ? (
                <Tooltip title="Copy summary">
                  <IconButton size="small" onClick={() => copyText(selectedEndpoint.summary)}>
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              sx={{ p: 2.2 }}
            >
              {selectedEndpoint.summary ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                  {selectedEndpoint.summary}
                </ReactMarkdown>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No endpoint summary has been generated for this route yet.
                </Typography>
              )}
            </SectionCard>

            <SectionCard
              title="Parameter Surface"
              subtitle="Inputs observed on the route during enumeration and testing."
              action={selectedEndpoint.paramTotal ? (
                <Tooltip title="Copy parameter JSON">
                  <IconButton
                    size="small"
                    onClick={() =>
                      copyText(
                        JSON.stringify(
                          {
                            body: selectedEndpoint.bodyParams,
                            query: selectedEndpoint.queryParams,
                            path: selectedEndpoint.pathParams,
                          },
                          null,
                          2
                        )
                      )
                    }
                  >
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
              sx={{ p: 2.2 }}
            >
              <Grid container spacing={1.2}>
                <Grid item xs={12} md={4}>
                  <ParamBlock title="Body Parameters" items={selectedEndpoint.bodyParams} color="#7b1fa2" />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ParamBlock title="Query Parameters" items={selectedEndpoint.queryParams} color={PRIMARYCOLOR} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ParamBlock title="Path Parameters" items={selectedEndpoint.pathParams} color="#ef6c00" />
                </Grid>
              </Grid>
            </SectionCard>

            <SectionCard
              title="Likely Attack Paths"
              subtitle="Modeled routes and verification candidates tied to this endpoint."
              sx={{ p: 2.2 }}
            >
              {selectedEndpoint.likelyAttacks.length ? (
                <Stack spacing={1.05}>
                  {selectedEndpoint.likelyAttacks.slice(0, 8).map((attack, index) => (
                    <Box
                      key={`${attack?.name || 'attack'}-${index}`}
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: alpha('#455a64', 0.12),
                        bgcolor: alpha('#455a64', 0.035),
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#10243E' }}>
                          {attack?.name || `Attack path ${index + 1}`}
                        </Typography>
                        <Chip
                          size="small"
                          label={`Score ${num(attack?.score, 0)}`}
                          sx={{ fontWeight: 800, bgcolor: alpha('#455a64', 0.12), color: '#455a64' }}
                        />
                      </Stack>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
                        {(Array.isArray(attack?.why) ? attack.why.join(' ') : String(attack?.why || '')).trim() ||
                          'Threat-model evidence is available on the attack path page for this endpoint.'}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No prioritized attack paths are attached to this endpoint yet.
                </Typography>
              )}

              {selectedEndpoint.threatNarrative ? (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#10243E', mb: 1 }}>
                    Threat Narrative
                  </Typography>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {selectedEndpoint.threatNarrative}
                  </ReactMarkdown>
                </>
              ) : null}
            </SectionCard>

            <SectionCard
              title="Validated Findings"
              subtitle="Confirmed issues already tied back to this endpoint."
              sx={{ p: 2.2 }}
              action={selectedEndpoint.totalFindings ? (
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<ArrowOutwardRoundedIcon />}
                  onClick={() => navigate(`/pentest/web/exploits/${pentest_id}`)}
                  sx={{ borderRadius: 999 }}
                >
                  Open Findings
                </Button>
              ) : null}
            >
              {drawerFindingRows.length ? (
                <Stack spacing={1}>
                  {drawerFindingRows.slice(0, 10).map((finding) => (
                    <Box
                      key={finding.id}
                      sx={{
                        p: 1.45,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: alpha(SEVERITY_META[finding.severity].color, 0.12),
                        bgcolor: alpha(SEVERITY_META[finding.severity].color, 0.04),
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#10243E' }}>
                          {finding.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={SEVERITY_META[finding.severity].label}
                          sx={{
                            fontWeight: 800,
                            bgcolor: alpha(SEVERITY_META[finding.severity].color, 0.12),
                            color: SEVERITY_META[finding.severity].color,
                          }}
                        />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No validated findings are linked to this endpoint yet.
                </Typography>
              )}
            </SectionCard>
          </Stack>
        ) : null}
      </Drawer>
    </Box>
  );
};

export default WebPentestAssets;

